import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Tab,
  MenuItem,
  IconButton,
  Menu,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useMediaState } from '../../context/media-state-context'
import { useAuth } from '../../context/AuthContext'
import { useBrowseHistory } from '../../context/browse-history-context'
import { useSnackbar } from '../../context/snackbar-context'
import settingsApi, { PinnedMediaItem } from '../../client/settings-api'
import LargeCard from '../../components/cards/large-card'
import MenuIcon from '@mui/icons-material/Menu'
import {
  ItemsBox,
  LowerProfileMenuItemsBox,
  StyledTabsForProfile,
} from './profile-styles'
import EmptyState from '../../utils/empty-state'
import { CenterBox } from '../../utils/center-box'

type TabKey = 'watched' | 'watchlist' | 'favorites' | 'recent'
type HistoryFilter = 'all' | 'shows' | 'movies'
type SortKey =
  | 'recent'
  | 'title'
  | 'year'
  | 'lastAdded'
  | 'lastEpisode'
  | 'finishedShows'
  | 'unfinishedShows'

const SegmentedControl = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: theme.palette.action.hover,
  borderRadius: 20,
  padding: 4,
  gap: 4,
}))

const SegmentedOption = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  borderRadius: 16,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: selected ? theme.palette.background.paper : 'transparent',
  boxShadow: selected ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
  transition: 'all 0.15s ease',
}))

const ProfileTabs = styled(StyledTabsForProfile)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const FilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}))

const ClearAllButton = styled('button')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 16,
  padding: theme.spacing(0.5, 1.5),
  fontSize: 13,
  cursor: 'pointer',
  color: theme.palette.text.secondary,
  backgroundColor: 'transparent',
  '&:hover': {
    color: theme.palette.text.primary,
    borderColor: '#8B1E3F',
  },
}))

const RemovableCard = styled(Box)({
  position: 'relative',
  '&:hover .recent-remove': { opacity: 1 },
})

const CardRemoveButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 2,
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  lineHeight: 1,
  color: theme.palette.common.white,
  backgroundColor: 'rgba(0,0,0,0.7)',
  opacity: 0,
  transition: 'opacity 0.15s ease',
  '&:hover': { backgroundColor: '#8B1E3F' },
}))

const TabLabelText = styled('span')({
  display: 'inline',
  whiteSpace: 'nowrap',
  color: 'inherit',
  fontWeight: 'inherit',
})

const TabLabel: React.FC<{ label: string; count?: number }> = ({
  label,
  count,
}) => (
  <TabLabelText>
    {label}
    {count !== undefined && ` - ${count}`}
  </TabLabelText>
)

const MAX_PINNED = 5

export const LowerProfile: React.FC = () => {
  const { mediaState, isLoaded } = useMediaState()
  const { getAccessToken, isAuthenticated } = useAuth()
  const {
    recentViews,
    isLoaded: recentViewsLoaded,
    removeView,
    clearViews,
  } = useBrowseHistory()
  const { enqueueSnackbar } = useSnackbar()

  const [tab, setTab] = useState<TabKey>('watched')
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('all')
  const [sort, setSort] = useState<SortKey>('recent')
  const [pinnedMedia, setPinnedMedia] = useState<PinnedMediaItem[]>([])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    ;(async () => {
      try {
        const token = await getAccessToken()
        const response = await settingsApi.get(token)
        if (!cancelled) setPinnedMedia(response.data.pinnedMedia ?? [])
      } catch {
        /* pins stay empty - toggling still works and saves */
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const isPinned = (item: { tmdbId: number; mediaType?: string }) =>
    pinnedMedia.some(
      (pinnedItem) =>
        pinnedItem.tmdbId === item.tmdbId &&
        pinnedItem.mediaType?.toLowerCase() === item.mediaType?.toLowerCase(),
    )

  const togglePin = async (item: {
    tmdbId: number
    mediaType?: string
    title: string
    posterPath?: string | null
    releaseYear?: number | null
  }) => {
    let next: PinnedMediaItem[]
    if (isPinned(item)) {
      next = pinnedMedia.filter(
        (pinnedItem) =>
          !(
            pinnedItem.tmdbId === item.tmdbId &&
            pinnedItem.mediaType?.toLowerCase() ===
              item.mediaType?.toLowerCase()
          ),
      )
    } else {
      if (pinnedMedia.length >= MAX_PINNED) {
        enqueueSnackbar(
          `You can pin up to ${MAX_PINNED} titles - unpin one first`,
          {
            variant: 'error',
          },
        )
        return
      }
      next = [
        ...pinnedMedia,
        {
          mediaType:
            item.mediaType?.toLowerCase() === 'show' ? 'Show' : 'Movie',
          tmdbId: item.tmdbId,
          title: item.title,
          posterPath: item.posterPath ?? undefined,
          releaseYear: item.releaseYear ?? undefined,
        },
      ]
    }

    const previous = pinnedMedia
    setPinnedMedia(next)
    try {
      const token = await getAccessToken()
      await settingsApi.update(token, { pinnedMedia: next })
    } catch {
      setPinnedMedia(previous)
      enqueueSnackbar('Could not update your showcase. Please try again.', {
        variant: 'error',
      })
    }
  }

  const items = useMemo(() => {
    // Recently viewed comes from the browse-history provider, already ordered
    // by last viewed; reshape it to the common card fields.
    if (tab === 'recent') {
      let result = recentViews.map((view) => ({
        tmdbId: view.tmdbId,
        mediaType: view.mediaType,
        title: view.title,
        posterPath: view.posterPath,
        releaseYear: view.releaseYear,
        genres: view.genres,
      }))
      if (sort === 'title')
        result = [...result].sort((a, b) => a.title.localeCompare(b.title))
      if (sort === 'year')
        result = [...result].sort(
          (a, b) => (b.releaseYear ?? 0) - (a.releaseYear ?? 0),
        )
      return result
    }

    if (!mediaState) return []

    const source =
      tab === 'watched'
        ? mediaState.history
        : tab === 'watchlist'
          ? mediaState.watchlist
          : mediaState.favorites

    let result = [...source]

    if (tab === 'watched') {
      if (historyFilter === 'movies') {
        result = result.filter((i) => i.mediaType?.toLowerCase() === 'movie')
      }

      if (historyFilter === 'shows') {
        result = result.filter((i) => i.mediaType?.toLowerCase() === 'show')
      }
    }

    if (tab === 'watched') {
      if (sort === 'recent') {
        result.sort(
          (a, b) =>
            new Date(b.lastWatchedOnUtc ?? 0).getTime() -
            new Date(a.lastWatchedOnUtc ?? 0).getTime(),
        )
      }
      if (sort === 'lastAdded') {
        result.sort(
          (a, b) =>
            new Date(b.lastWatchedOnUtc ?? 0).getTime() -
            new Date(a.lastWatchedOnUtc ?? 0).getTime(),
        )
      }
      if (sort === 'lastEpisode') {
        result.sort(
          (a, b) =>
            new Date((b as any).lastEpisodeAddedUtc ?? 0).getTime() -
            new Date((a as any).lastEpisodeAddedUtc ?? 0).getTime(),
        )
      }
      if (sort === 'finishedShows') {
        result = result.filter(
          (i) =>
            i.mediaType?.toLowerCase() === 'show' &&
            i.episodesWatchedCount === i.numberOfEpisodes,
        )
      }
      if (sort === 'unfinishedShows') {
        result = result.filter(
          (i) =>
            i.mediaType?.toLowerCase() === 'show' &&
            i.episodesWatchedCount !== i.numberOfEpisodes,
        )
      }
    }

    if (sort === 'title') {
      result.sort((a, b) => a.title.localeCompare(b.title))
    }

    if (sort === 'year') {
      result.sort((a, b) => (b.releaseYear ?? 0) - (a.releaseYear ?? 0))
    }

    return result
  }, [mediaState, tab, historyFilter, sort, recentViews])

  return (
    <Box>
      <ProfileTabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
      >
        <Tab
          value='watched'
          label={
            <TabLabel label='History' count={mediaState?.history.length} />
          }
        />
        <Tab
          value='favorites'
          label={
            <TabLabel label='Favorites' count={mediaState?.favorites.length} />
          }
        />
        <Tab
          value='watchlist'
          label={
            <TabLabel label='Watchlist' count={mediaState?.watchlist.length} />
          }
        />
        <Tab
          value='recent'
          label={
            <TabLabel
              label='Recently viewed'
              count={recentViewsLoaded ? recentViews.length : undefined}
            />
          }
        />
      </ProfileTabs>

      <LowerProfileMenuItemsBox>
        <FilterRow>
          {tab === 'recent' && items.length > 0 && (
            <ClearAllButton
              onClick={async () => {
                try {
                  await clearViews()
                  enqueueSnackbar('Browse history cleared', {
                    variant: 'success',
                  })
                } catch {
                  enqueueSnackbar('Could not clear browse history', {
                    variant: 'error',
                  })
                }
              }}
            >
              Clear all
            </ClearAllButton>
          )}

          {tab === 'watched' && (
            <SegmentedControl>
              {(['all', 'shows', 'movies'] as const).map((value) => (
                <SegmentedOption
                  key={value}
                  selected={historyFilter === value}
                  onClick={() => setHistoryFilter(value)}
                >
                  {value === 'all'
                    ? 'All'
                    : value === 'shows'
                      ? 'Shows'
                      : 'Movies'}
                </SegmentedOption>
              ))}
            </SegmentedControl>
          )}
        </FilterRow>

        <Tooltip title='Sort By'>
          <IconButton
            size='small'
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
          {tab === 'watched' ? (
            <>
              <MenuItem
                onClick={() => {
                  setSort('title')
                  setAnchorEl(null)
                }}
              >
                Name
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSort('lastAdded')
                  setAnchorEl(null)
                }}
              >
                Last Added
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSort('lastEpisode')
                  setAnchorEl(null)
                  setHistoryFilter('shows')
                }}
              >
                Last Watched Episode
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSort('finishedShows')
                  setAnchorEl(null)
                  setHistoryFilter('shows')
                }}
              >
                Finished Shows
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSort('unfinishedShows')
                  setAnchorEl(null)
                  setHistoryFilter('shows')
                }}
              >
                Non-Finished Shows
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem
                onClick={() => {
                  setSort('title')
                  setAnchorEl(null)
                }}
              >
                Title
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSort('year')
                  setAnchorEl(null)
                }}
              >
                Release Year
              </MenuItem>
            </>
          )}
        </Menu>
      </LowerProfileMenuItemsBox>

      {!isLoaded ? (
        <CenterBox>
          <CircularProgress size={48} />
        </CenterBox>
      ) : items.length === 0 ? (
        <EmptyState title='Nothing here yet' />
      ) : (
        <ItemsBox>
          {items.map((item: any) => {
            const card = (
              <LargeCard
                key={`${tab}-${item.tmdbId}`}
                id={item.tmdbId}
                tmdbId={item.tmdbId}
                type={
                  item.mediaType?.toLowerCase() === 'show' ? 'show' : 'movie'
                }
                title={item.title}
                year={item.releaseYear ? String(item.releaseYear) : undefined}
                posterUrl={item.posterPath ?? undefined}
                genres={item.genres ?? []}
                lastWatchedOnUtc={item.lastWatchedOnUtc}
                episodesWatchedCount={item.episodesWatchedCount}
                episodesTotal={
                  item.numberOfEpisodes ?? item.episodesWatchedCount
                }
                pinned={isPinned(item)}
                onTogglePin={() => togglePin(item)}
              />
            )
            if (tab !== 'recent') return card
            return (
              <RemovableCard key={`recent-${item.mediaType}-${item.tmdbId}`}>
                {card}
                <CardRemoveButton
                  className='recent-remove'
                  onClick={() => removeView(item.mediaType, item.tmdbId)}
                  aria-label={`Remove ${item.title} from recently viewed`}
                >
                  ✕
                </CardRemoveButton>
              </RemovableCard>
            )
          })}
        </ItemsBox>
      )}
    </Box>
  )
}
